/**
 * One entry on the do-not-call list.
 *
 * The number is the key, but it is not the answer to the question the screen is
 * asked. "Who is on this list" is asked about companies, so the row leads with
 * the name we know and carries the number under it - and when there is no name,
 * it says so rather than leaving the line blank, because "we have never called
 * this number" and "we forgot to show the name" look identical when empty.
 *
 * The number is NOT struck through here. Everywhere else in the CRM a struck
 * number means "careful, this one is on the list"; on the list itself that is
 * every row, and a page of struck-out text reads as broken rather than as a
 * warning.
 */
import { Link } from "react-router-dom";
import { Ban, Copy, MoreHorizontal, PhoneOff, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { PhoneSuppression } from "../../../../lib/callSuppression";
import { formatPhone } from "../../../../lib/phone";
import { Pop, useClosePops } from "../../../shell/Pop";
import { StatusPill, fmtStamp, useToast } from "../../../ui";
import { sourceTone } from "./rows";

export function DoNotCallRow({
  row,
  cols,
  canRelease,
  onRemove,
}: {
  row: PhoneSuppression;
  /** The grid tracks, shared with the head so the two cannot drift apart. */
  cols: string;
  /** May this viewer take a number back OFF the list (`manage:crm.calls`)? */
  canRelease: boolean;
  onRemove: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();

  const party = row.party;
  const lastCallId = party?.lastCallId ?? null;
  // Permanent is the person's own decision, so nobody here may undo it - the
  // endpoint refuses it too, and a menu item the API is guaranteed to reject is
  // a promise the software cannot keep.
  const canRemove = canRelease && !row.permanent;

  async function copyValue() {
    close();
    try {
      await navigator.clipboard.writeText(row.value);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    <div className="crm-list-row" style={{ gridTemplateColumns: cols }}>
      <span className="flex min-w-0 items-center gap-3">
        <span className="crm-av crm-av-soft">
          <PhoneOff className="size-4" />
        </span>
        <span className="min-w-0">
          <span className="crm-prow-n crm-trunc block">
            {party?.companyName ?? t("calls.suppression.unknownParty")}
          </span>
          <span className="crm-prow-s crm-tnum crm-trunc block">{formatPhone(row.value)}</span>
        </span>
      </span>

      <span>
        <StatusPill tone={sourceTone(row.source)}>{t(`calls.suppression.source.${row.source}`)}</StatusPill>
      </span>

      <span className="crm-mut crm-trunc text-[12.5px]">{row.note ?? row.reason ?? "-"}</span>

      {/* How many times we rang them before they asked us to stop, and the way
          into the newest of those calls. A number nobody ever called - added by
          hand, or imported - has neither, and says so. */}
      <span className="crm-mut crm-tnum text-[12.5px]">
        {party && party.calls > 0 ? (
          lastCallId ? (
            <Link to={`/calls/${lastCallId}`} className="text-primary hover:underline">
              {t("calls.suppression.callCount", { n: party.calls })}
            </Link>
          ) : (
            t("calls.suppression.callCount", { n: party.calls })
          )
        ) : (
          t("calls.suppression.noCalls")
        )}
      </span>

      <span className="crm-mut crm-trunc text-[12.5px]">
        {row.createdByName ? `${row.createdByName} · ` : ""}
        {fmtStamp(row.createdAt)}
      </span>

      <span className="justify-self-end">
        <Pop
          trigger={(open) => (
            <button
              type="button"
              className={cn("crm-cb", open && "is-on")}
              title={t("common.more")}
              aria-label={t("common.more")}
              aria-haspopup="menu"
              aria-expanded={open}
            >
              <MoreHorizontal className="size-4" />
            </button>
          )}
        >
          <button type="button" className="crm-mi" onClick={() => void copyValue()}>
            <Copy className="size-4" />
            {t("calls.suppression.copyValue")}
          </button>

          {canRemove ? (
            <>
              <div className="crm-menu-sep" />
              <button
                type="button"
                className="crm-mi crm-mi-danger"
                onClick={() => {
                  close();
                  onRemove();
                }}
              >
                <Trash2 className="size-4" />
                {t("calls.suppression.remove")}
              </button>
            </>
          ) : row.permanent ? (
            // Said rather than left out. An operator who cannot find the Remove
            // item assumes a permission problem and goes looking for somebody
            // to ask; this tells them there is nobody to ask.
            <>
              <div className="crm-menu-sep" />
              <div className="crm-menu-head">
                <div className="crm-menu-s">{t("calls.suppression.permanentHint")}</div>
              </div>
            </>
          ) : null}
        </Pop>
      </span>
    </div>
  );
}
